import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, tokens, Skeleton, SkeletonItem } from '@fluentui/react-components';
import {
  TextBulletListSquareRegular,
  AddRegular,
  MicRegular,
  ArrowRightFilled,
} from '@fluentui/react-icons';
import CopilotIcon from '../../../assets/office/ribbon/Copilot.svg';
import { useWordSlateEditor } from './WordSlateEditorContext';
import { llmService } from '../../../llm/services/LLMEnhancementService';
import { azureOpenAIService } from '../../../llm/services/AzureOpenAIService';
import { DEFAULT_SUMMARY_TEXT } from './defaultSummary';

type TopOfDocState = 'loading' | 'generating' | 'rest';

interface TopOfDocStyleBProps {
  summaryText?: string;
  onCopilotClick?: () => void;
  onSummaryClick?: (summary: string) => void;
  onMessageSend?: (message: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '0', // Use margin on copilotInput instead for animation
    alignItems: 'center',
    justifyContent: 'center',
    width: '816px', // Fixed to Slate editor width
    maxWidth: '816px',
    minWidth: '816px',
    padding: '0',
    boxSizing: 'border-box',
    margin: '0 auto', // Center in parent container
  },
  
  // Summary card - flex: 2 (2:1 ratio with Message Copilot)
  summaryCard: {
    flex: '2 1 0',
    minWidth: '0',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '10px 14px 10px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxSizing: 'border-box',
    opacity: 1,
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Summary content wrapper to handle text truncation
  summaryContent: {
    flex: '1 1 0',
    minWidth: '0',
    overflow: 'hidden',
  },
  
  // Summary card hidden when input is focused - completely hidden
  summaryCardHidden: {
    display: 'none',
  },
  
  // Copilot input - flex: 1 (2:1 ratio with Summary card)
  copilotInput: {
    flex: '1 1 0',
    minWidth: '0',
    marginLeft: '12px', // Gap between Summary and input
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '10px 14px 10px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Copilot input expanded when focused (takes full width)
  copilotInputExpanded: {
    flex: '1 1 100%',
    marginLeft: '0 !important', // Remove gap when expanded
    width: '100%',
  },
  
  // Icon styling
  icon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
  },
  
  copilotIconImg: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    cursor: 'pointer',
    borderRadius: '4px',
    padding: '0',
    marginRight: '-2px',
    transition: 'background-color 0.1s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  
  // Icon title container
  iconTitle: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    flexShrink: 0,
  },
  
  // Text styles
  titleText: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    fontFamily: '"Segoe UI", sans-serif',
  },
  
  summaryText: {
    flex: '1 1 0',
    minWidth: '0',
    maxWidth: '100%',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: '"Segoe UI", sans-serif',
    display: 'block',
  },
  
  generatingText: {
    flex: '1 0 0',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: '"Segoe UI", sans-serif',
    fontStyle: 'italic',
  },
  
  placeholderText: {
    flex: '1 1 0',
    minWidth: '0',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: '"Segoe UI", sans-serif',
  },
  
  // Message Copilot input field
  messageInput: {
    flex: '1 1 0',
    minWidth: '0',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    height: '20px',
    padding: 0,
    margin: 0,
    color: tokens.colorNeutralForeground1,
    fontFamily: '"Segoe UI", sans-serif',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: tokens.colorNeutralForeground4,
    },
  },
  
  // Send button (circular blue button with arrow)
  sendButton: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    border: 'none',
    padding: 0,
    transition: 'background-color 0.1s ease',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    '&:active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
    },
  },
  
  sendIcon: {
    width: '12px',
    height: '12px',
    color: 'white',
  },
  
  // Skeleton styles
  skeletonContainer: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  
  // Skeleton container for input - icons on the right
  skeletonContainerInput: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    width: '100%',
  },
  
  // Skeleton items that should be pushed to the right
  skeletonRightGroup: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  
  skeletonIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
  },
  
  skeletonText: {
    width: '80px',
    height: '16px',
    borderRadius: '4px',
  },
  
  skeletonTextLong: {
    width: '120px',
    height: '16px',
    borderRadius: '4px',
  },
});

export const TopOfDocStyleB: React.FC<TopOfDocStyleBProps> = ({ 
  summaryText: propSummaryText,
  onCopilotClick,
  onSummaryClick,
  onMessageSend
}) => {
  const styles = useStyles();
  const [state, setState] = useState<TopOfDocState>('loading');
  const [summaryText, setSummaryText] = useState<string>(propSummaryText || '');
  const [messageText, setMessageText] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { commands } = useWordSlateEditor();

  // Handle send message
  const handleSendMessage = useCallback(() => {
    if (messageText.trim()) {
      onMessageSend?.(messageText.trim());
      setMessageText('');
    }
  }, [messageText, onMessageSend]);

  // Handle key press in input
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && messageText.trim()) {
      handleSendMessage();
    }
  }, [messageText, handleSendMessage]);

  // Generate summary using LLM
  const generateSummary = useCallback(async () => {
    try {
      // Get document text from Slate editor
      const documentText = commands.getDocumentText();
      
      if (!documentText || documentText.length < 50) {
        setSummaryText('Document is too short to summarize.');
        setState('rest');
        return;
      }

      // Priority 1: Try Azure OpenAI if configured
      if (azureOpenAIService.isConfigured()) {
        try {
          console.log('Using Azure OpenAI for summary generation...');
          const response = await azureOpenAIService.generateSummary(documentText);
          setSummaryText(response.trim());
          setState('rest');
          return;
        } catch (error) {
          console.error('Azure OpenAI failed, trying fallback:', error);
        }
      }

      // Priority 2: Try Microsoft LLM if enabled and authenticated
      const isAuthenticated = await llmService.isAuthenticated();
      const config = llmService.getConfig();
      
      if (config.isEnabled && isAuthenticated) {
        try {
          const prompt = `Please summarize the following document.

Requirements:
1. Start directly with a summary paragraph (at least 400 characters) that captures the key points. Do NOT include any heading like "Overview" or "Summary". Do NOT mention or repeat the document title - the reader already knows it.
2. After the first paragraph, summarize each major section directly without unnecessary labels.
3. Be concise and direct. Every sentence should provide value. No filler phrases like "This document discusses..." or "The article covers...".
4. Use clear paragraph breaks between sections.
5. Provide complete sentences, no truncation.

Document:
${documentText}`;
          const response = await llmService.generateResponse(prompt);
          setSummaryText(response.trim());
          setState('rest');
          return;
        } catch (error) {
          console.error('Microsoft LLM failed:', error);
        }
      }

      // Fallback: use default summary when AI is not available
      console.log('Using default summary (AI not available)');
      setSummaryText(DEFAULT_SUMMARY_TEXT);
      setState('rest');
    } catch (error) {
      console.error('Failed to generate summary:', error);
      // Fallback: use default summary
      console.log('Using default summary (error occurred)');
      setSummaryText(DEFAULT_SUMMARY_TEXT);
      setState('rest');
    }
  }, [commands]);

  // Loading states and summary generation
  useEffect(() => {
    // After 1 second, transition from loading to generating
    const loadingTimer = setTimeout(() => {
      setState('generating');
      // Start generating summary
      generateSummary();
    }, 1000);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, [generateSummary]);

  const renderSkeletonCopilotInput = () => (
    <div className={styles.copilotInput} style={{ justifyContent: 'space-between' }}>
      <Skeleton style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <SkeletonItem className={styles.skeletonIcon} />
        <SkeletonItem className={styles.skeletonTextLong} />
      </Skeleton>
      <Skeleton style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <SkeletonItem className={styles.skeletonIcon} />
        <SkeletonItem className={styles.skeletonIcon} />
      </Skeleton>
    </div>
  );

  // Loading state - all skeletons
  if (state === 'loading') {
    return (
      <div className={styles.container}>
        {/* Summary skeleton */}
        <div className={styles.summaryCard}>
          <Skeleton>
            <div className={styles.skeletonContainer}>
              <SkeletonItem className={styles.skeletonIcon} />
              <SkeletonItem className={styles.skeletonText} />
            </div>
          </Skeleton>
        </div>
        
        {/* Copilot input skeleton */}
        {renderSkeletonCopilotInput()}
      </div>
    );
  }

  // Generating state - summary generating, others loaded
  if (state === 'generating') {
    return (
      <div className={styles.container}>
        {/* Summary - generating */}
        <div className={`${styles.summaryCard} ${isInputFocused ? styles.summaryCardHidden : ''}`}>
          <div className={styles.iconTitle}>
            <TextBulletListSquareRegular className={styles.icon} />
            <span className={styles.titleText}>Generating summary...</span>
          </div>
        </div>
        
        {/* Copilot input - loaded */}
        <div className={`${styles.copilotInput} ${isInputFocused ? styles.copilotInputExpanded : ''}`}>
          <AddRegular className={styles.icon} />
          <input 
            type="text"
            className={styles.messageInput}
            placeholder="Message Copilot"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
          {messageText.trim() ? (
            <button className={styles.sendButton} onClick={handleSendMessage}>
              <ArrowRightFilled className={styles.sendIcon} />
            </button>
          ) : (
            <MicRegular className={styles.icon} />
          )}
          <img 
            src={CopilotIcon} 
            alt="Copilot" 
            className={styles.copilotIconImg}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Copilot icon clicked (generating), onCopilotClick:', onCopilotClick);
              if (onCopilotClick) {
                onCopilotClick();
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Rest state - all loaded
  return (
    <div className={styles.container}>
      {/* Summary - loaded - click to show full summary in Copilot pane */}
      {/* Summary - loaded - click to show full summary in Copilot pane */}
      <div 
        className={`${styles.summaryCard} ${isInputFocused ? styles.summaryCardHidden : ''}`}
        onClick={() => onSummaryClick?.(summaryText)}
      >
        <div className={styles.iconTitle}>
          <TextBulletListSquareRegular className={styles.icon} />
          <span className={styles.titleText}>Summary</span>
        </div>
        <div className={styles.summaryContent}>
          <span className={styles.summaryText}>{summaryText}</span>
        </div>
      </div>
      
      {/* Copilot input */}
      <div className={`${styles.copilotInput} ${isInputFocused ? styles.copilotInputExpanded : ''}`}>
        <AddRegular className={styles.icon} />
        <input 
          type="text"
          className={styles.messageInput}
          placeholder="Message Copilot"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />
        {messageText.trim() ? (
          <button className={styles.sendButton} onClick={handleSendMessage}>
            <ArrowRightFilled className={styles.sendIcon} />
          </button>
        ) : (
          <MicRegular className={styles.icon} />
        )}
        <img 
          src={CopilotIcon} 
          alt="Copilot" 
          className={styles.copilotIconImg}
          onClick={(e) => {
            e.stopPropagation();
            console.log('Copilot icon clicked, onCopilotClick:', onCopilotClick);
            if (onCopilotClick) {
              onCopilotClick();
            }
          }}
        />
      </div>
    </div>
  );
};

export default TopOfDocStyleB;
